/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useMemo, ReactNode, FC } from "react";

export type LockReason = "generating" | "chat-responding" | null;

interface CanvasLockContextValue {
  isLocked: boolean;
  lockReason: LockReason;
  // Internal setters (used by providers)
  setIsGenerating: (isGenerating: boolean) => void;
  setIsChatResponding: (isResponding: boolean) => void;
}

// Export context for components that need direct access (e.g., ChatLockSync)
export const CanvasLockContext = createContext<CanvasLockContextValue | null>(null);

interface CanvasLockProviderProps {
  children: ReactNode;
  /** Initial generation state - typically from useAIDocumentGenerationStatus */
  initialIsGenerating?: boolean;
}

/**
 * Provider that manages the canvas lock state.
 * Lock reasons (in priority order):
 * 1. Report is being generated
 * 2. Chat is responding
 */
export const CanvasLockProvider: FC<CanvasLockProviderProps> = ({
  children,
  initialIsGenerating = false,
}) => {
  const [isGenerating, setIsGenerating] = useState(initialIsGenerating);
  const [isChatResponding, setIsChatResponding] = useState(false);

  const value = useMemo<CanvasLockContextValue>(() => {
    // Determine lock reason by priority
    let lockReason: LockReason = null;
    if (isGenerating) {
      lockReason = "generating";
    } else if (isChatResponding) {
      lockReason = "chat-responding";
    }

    return {
      isLocked: lockReason !== null,
      lockReason,
      setIsGenerating,
      setIsChatResponding,
    };
  }, [isGenerating, isChatResponding]);

  return <CanvasLockContext.Provider value={value}>{children}</CanvasLockContext.Provider>;
};

/**
 * Hook to access the canvas lock state.
 * Must be used within a CanvasLockProvider.
 */
export const useCanvasLock = (): CanvasLockContextValue => {
  const context = useContext(CanvasLockContext);
  if (!context) {
    throw new Error("useCanvasLock must be used within a CanvasLockProvider");
  }
  return context;
};

/**
 * Hook to check if canvas is locked (safe version that returns false if no provider).
 * Useful for components that may be rendered outside the lock context.
 */
export const useCanvasLockSafe = (): { isLocked: boolean; lockReason: LockReason } => {
  const context = useContext(CanvasLockContext);
  return {
    isLocked: context?.isLocked ?? false,
    lockReason: context?.lockReason ?? null,
  };
};
